// Улучшенный AdaptiveCourseSidebar.jsx с плавными анимациями
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const AdaptiveCourseSidebar = ({
  courseName,
  modules,
  activeModule,
  onSelectModule,
  onShowOverview,
  sidebarCollapsed,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Управляем видимостью для плавной анимации
  useEffect(() => {
    const shouldShow = isMobile ? isMobileMenuOpen : !sidebarCollapsed;
    
    if (shouldShow) {
      // Показываем элемент сразу, чтобы анимация началась
      setIsVisible(true);
    } else {
      // Задержка перед скрытием для завершения анимации закрытия
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isMobile, isMobileMenuOpen, sidebarCollapsed]);

  // Определяем, должен ли сайдбар быть открыт
  const shouldBeOpen = isMobile ? isMobileMenuOpen : !sidebarCollapsed;

  // Всегда рендерим компонент если он должен быть видим или анимируется
  if (!isVisible) return null;

  return (
    <>
      {/* Overlay для мобильных с плавным появлением */}
      {isMobile && (
        <div
          className={`fixed inset-0 bg-black z-40 lg:hidden transition-opacity duration-300 ease-in-out ${
            isMobileMenuOpen 
              ? 'opacity-50 pointer-events-auto' 
              : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside 
        className={`
          ${isMobile
            ? 'fixed top-12 left-0 z-50 h-full w-80 max-w-[85vw] transform'
            : 'relative'
          }
          bg-white border-r border-gray-200 h-screen overflow-y-auto
          transition-all duration-300 ease-in-out
          ${isMobile 
            ? (shouldBeOpen 
                ? 'translate-x-0' 
                : '-translate-x-full'
              )
            : (shouldBeOpen 
                ? 'w-64 opacity-100' 
                : 'w-0 border-r-0 opacity-0'
              )
          }
        `}
        style={isMobile ? {
          transform: shouldBeOpen ? 'translateX(0)' : 'translateX(-100%)'
        } : {}}
      >
        <div className={`p-4 transition-opacity duration-300 ease-in-out ${
          shouldBeOpen ? 'opacity-100 delay-100' : 'opacity-0'
        }`}>
          {/* Шапка с кнопкой закрытия для мобильных */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-green-600 pr-2 min-w-0 truncate">
              {courseName}
            </h1>
            {isMobile && (
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-md lg:hidden flex-shrink-0 
                         transition-colors duration-200 hover:scale-105 transform"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <button
            className="w-full bg-white text-green-600 border border-green-600 py-2 px-3 
                     rounded-md hover:bg-green-600 hover:text-white transition-all duration-200 
                     mb-6 hover:shadow-md transform hover:-translate-y-0.5"
            onClick={() => {
              onShowOverview();
              if (isMobile) setIsMobileMenuOpen(false);
            }}
          >
            Обзор курса
          </button>

          <h2 className="text-md font-semibold text-gray-800 mb-3">
            Разделы
          </h2>

          <ul className="space-y-2">
            {Object.entries(modules).map(([moduleId, module], index) => (
              <li
                key={moduleId}
                className={`cursor-pointer px-3 py-3 rounded-md text-sm font-medium 
                          transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-sm
                          ${activeModule === moduleId
                            ? "bg-green-600 text-white shadow-md"
                            : "text-gray-700 hover:bg-gray-100"
                          }`}
                style={{
                  transitionDelay: shouldBeOpen ? `${index * 30 + 150}ms` : '0ms'
                }}
                onClick={() => {
                  onSelectModule(moduleId);
                  if (isMobile) setIsMobileMenuOpen(false);
                }}
                title={module.title}
              >
                <span className="block truncate">
                  {module.title}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </>
  );
};

export default AdaptiveCourseSidebar;